import React, { useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import ErrorMessage from "../helpers/ErrorMessage";
import { useLanguage } from "../helpers/LanguageContext";

const NewWorkModal = ({
  handleClose,
  onSubmit,
  parties,
  isCompany = false,
  isAdmin = false,
  partyId = "",
  onPartyChange,
  onAddParty,
  name,
  onNameChange
}) => {
  const { t } = useLanguage();
  const selectedClient = useSelector((state) => state.selectedClient);
  const [localName, setLocalName] = useState("");
  const [partySearch, setPartySearch] = useState("");
  const [partyDropdownOpen, setPartyDropdownOpen] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const hasPartySelector = parties !== undefined;
  const usesDefaultName = hasPartySelector && isCompany;
  const workName = usesDefaultName
    ? "C\u00e9ges r\u00f6gz\u00edt\u00e9s"
    : (name ?? localName);
  const searchTerm = partySearch.trim().toLocaleLowerCase();
  const filteredParties = hasPartySelector
    ? parties.filter(
        (party) =>
          String(party.name ?? "")
            .toLocaleLowerCase()
            .includes(searchTerm) || String(party.id) === String(partyId)
      )
    : [];
  const selectedParty = hasPartySelector
    ? parties.find((party) => String(party.id) === String(partyId))
    : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!workName.trim()) {
      setError(t("newWorkNameError"));
      return;
    }
    const id = hasPartySelector ? partyId : selectedClient;
    if (
      !id ||
      (hasPartySelector &&
        !parties.some((party) => String(party.id) === String(id)))
    ) {
      setError(t(isCompany ? "selectCarpenter" : "selectClient"));
      return;
    }
    const newWork = {
      name: workName.trim(),
      ...(hasPartySelector && isCompany
        ? { user: { userId: Number(id) } }
        : { client: { clientId: Number(id) } })
    };
    setSaving(true);
    setError("");
    try {
      await onSubmit(newWork);
      handleClose();
    } catch (error) {
      setError(t("newWorkServerError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {hasPartySelector && (
        <Form.Group className="mb-3">
          <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
            <Form.Label id="formWorkPartyLabel" className="mb-0">
              {t(isCompany ? "user" : "client")}
            </Form.Label>
            <Button
              variant="outline-primary"
              size="sm"
              type="button"
              onClick={onAddParty}
              disabled={saving}
            >
              {t(isCompany ? "createCarpenter" : "createClient")}
            </Button>
          </div>
          <Dropdown
            show={partyDropdownOpen}
            onToggle={setPartyDropdownOpen}
            autoClose="outside"
            className="w-100"
          >
            <Dropdown.Toggle
              variant="outline-secondary"
              className="w-100 d-flex align-items-center justify-content-between text-start bg-white"
              aria-labelledby="formWorkPartyLabel formWorkPartyValue"
              disabled={saving}
            >
              <span id="formWorkPartyValue" className="text-truncate">
                {selectedParty?.name ||
                  t(isCompany ? "selectCarpenter" : "selectClient")}
              </span>
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="w-100 p-2"
              style={{ maxHeight: "18rem", overflowY: "auto" }}
            >
              <Form.Control
                autoFocus
                type="search"
                aria-label={t("search")}
                placeholder={t("search")}
                value={partySearch}
                onChange={(event) => setPartySearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                disabled={saving}
              />
              <div className="mt-2">
                <Dropdown.Item
                  as="button"
                  type="button"
                  active={!partyId}
                  onClick={() => {
                    onPartyChange("");
                    setPartySearch("");
                    setPartyDropdownOpen(false);
                  }}
                >
                  {t(isCompany ? "selectCarpenter" : "selectClient")}
                </Dropdown.Item>
                {filteredParties.length ? (
                  filteredParties.map((party) => (
                    <Dropdown.Item
                      as="button"
                      type="button"
                      key={party.id}
                      active={String(party.id) === String(partyId)}
                      className="text-wrap"
                      onClick={() => {
                        onPartyChange(String(party.id));
                        setPartySearch("");
                        setPartyDropdownOpen(false);
                      }}
                    >
                      <span className="d-block">{party.name}</span>
                      <small className="d-block text-body-secondary">
                        {t(
                          isCompany ? "orderedWorkCount" : "assignedWorkCount"
                        )}
                        : {party.workCount ?? 0}
                      </small>
                      {isAdmin && party.carpenterName && (
                        <small className="d-block text-body-secondary">
                          {t("user")}: {party.carpenterName}
                        </small>
                      )}
                    </Dropdown.Item>
                  ))
                ) : (
                  <div className="px-3 py-2 small text-body-secondary">
                    {t("noSearchResults")}
                  </div>
                )}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </Form.Group>
      )}
      <Form.Group className="mb-3" controlId="formWorkName">
        <Form.Label>{t("newWorkName")}</Form.Label>
        <Form.Control
          type="text"
          placeholder={t("newWorkNamePlaceholder")}
          name="name"
          value={workName}
          disabled={saving}
          readOnly={usesDefaultName}
          onChange={(event) =>
            (onNameChange || setLocalName)(event.target.value)
          }
        />
      </Form.Group>
      {error && <ErrorMessage message={error} />}
      <Button variant="primary" type="submit" disabled={saving}>
        {t("newWorkSubmit")}
      </Button>
    </Form>
  );
};
export default NewWorkModal;
